import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUserCheck, FiUserX, FiMail, FiKey } from 'react-icons/fi';
import api from '../api';

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [teachersRes, classesRes] = await Promise.all([
        api.get('/api/users', { params: { role: 'teacher' } }),
        api.get('/api/class-groups')
      ]);
      setTeachers(teachersRes.data.users || []);
      setClasses(classesRes.data.classGroups || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTeacher(null);
    setFormData({ name: '', email: '', password: '', phone: '' });
    setShowModal(true);
  };

  const openEditModal = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      email: teacher.email,
      password: '',
      phone: teacher.phone || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTeacher) {
        // Update
        const updateData = { name: formData.name, phone: formData.phone };
        if (formData.password) updateData.password = formData.password;

        await api.put(`/api/users/${editingTeacher._id}`, updateData);
        alert('수정되었습니다');
      } else {
        // Create
        await api.post('/api/users', {
          ...formData,
          role: 'teacher'
        });
        alert('강사가 생성되었습니다');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const handleDelete = async (teacher) => {
    if (!window.confirm(`${teacher.name} 강사를 완전히 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;

    try {
      await api.delete(`/api/users/${teacher._id}?permanent=true`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const toggleActive = async (teacher) => {
    try {
      await api.put(`/api/users/${teacher._id}`, {
        isActive: !teacher.isActive
      });
      loadData();
    } catch (error) {
      alert('상태 변경에 실패했습니다');
    }
  };

  const getTeacherClasses = (teacherId) => {
    return classes.filter(c => c.teachers?.some(t =>
      (t._id || t) === teacherId
    ));
  };

  // Stats
  const stats = {
    total: teachers.length,
    active: teachers.filter(t => t.isActive).length,
    inactive: teachers.filter(t => !t.isActive).length
  };

  return (
    <div className="teachers-page">
      <div className="page-header">
        <h2>강사 관리</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FiPlus /> 강사 추가
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 강사</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">활성</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.inactive}</div>
          <div className="stat-label">비활성</div>
        </div>
      </div>

      {/* Teachers List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>이름</th>
              <th>이메일</th>
              <th>연락처</th>
              <th>담당 반</th>
              <th>상태</th>
              <th>가입일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading">로딩 중...</td></tr>
            ) : teachers.length === 0 ? (
              <tr><td colSpan="7" className="empty">등록된 강사가 없습니다</td></tr>
            ) : (
              teachers.map((teacher) => {
                const teacherClasses = getTeacherClasses(teacher._id);
                return (
                  <tr key={teacher._id}>
                    <td>
                      <div className="teacher-name">
                        <FiUserCheck className="icon" />
                        {teacher.name}
                      </div>
                    </td>
                    <td>{teacher.email}</td>
                    <td>{teacher.phone || '-'}</td>
                    <td>
                      {teacherClasses.length > 0 ? (
                        <div className="class-tags">
                          {teacherClasses.map(c => (
                            <span key={c._id} className="badge">{c.name}</span>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`status ${teacher.isActive ? 'active' : 'inactive'}`}>
                        {teacher.isActive ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td>{format(new Date(teacher.createdAt), 'yyyy-MM-dd')}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="icon-btn" onClick={() => openEditModal(teacher)} title="수정">
                          <FiEdit2 />
                        </button>
                        <button
                          className={`icon-btn ${teacher.isActive ? 'warning' : 'success'}`}
                          onClick={() => toggleActive(teacher)}
                          title={teacher.isActive ? '비활성화' : '활성화'}
                        >
                          {teacher.isActive ? <FiUserX /> : <FiUserCheck />}
                        </button>
                        <button className="icon-btn danger" onClick={() => handleDelete(teacher)} title="삭제">
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTeacher ? '강사 수정' : '강사 추가'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>이름 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="강사 이름"
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일 *</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    required
                    disabled={!!editingTeacher}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{editingTeacher ? '비밀번호 (변경시에만 입력)' : '비밀번호 *'}</label>
                <div className="input-with-icon">
                  <FiKey className="input-icon" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingTeacher ? '변경시에만 입력' : '비밀번호'}
                    required={!editingTeacher}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>연락처</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="010-0000-0000"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTeacher ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
